import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const ServicesPage = () => {
  const services = [
    {
      id: "kitchen",
      title: "Kitchen Renovation",
      description: "Transform your kitchen with our comprehensive renovation services, from countertop installation to complete custom redesigns.",
      image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/741kiolr_1000115910.jpg",
      features: [
        "Countertop installation",
        "Appliance setup & installation",
        "Custom kitchen redesigns",
        "Backsplash installation",
        "Cabinet installation & refinishing",
        "Lighting & electrical updates",
      ],
      featured: true,
    },
    {
      id: "bathroom",
      title: "Bathroom Remodeling",
      description: "From minor updates to complete overhauls, we transform bathrooms with expert craftsmanship and attention to detail.",
      image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/762zh6q9_1000109912.jpg",
      features: [
        "Complete bathroom overhauls",
        "Tile installation (floor & wall)",
        "Shower doors & enclosures",
        "Waterproofing solutions",
        "Fixture upgrades & selection",
        "Accessibility modifications",
      ],
      guide: "/guides/bathroom-renovation-cost",
      featured: true,
    },
    {
      id: "full-home",
      title: "General Contracting & Project Management",
      description: "Skilled oversight of all aspects of construction to ensure efficiency and quality on your renovation project.",
      image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/hv43d69t_Gemini_Generated_Image_nwkojrnwkojrnwko.png",
      features: [
        "Open floor plan conversions",
        "Room additions & extensions",
        "Structural modifications",
        "Complete interior remodels",
        "Energy efficiency upgrades",
        "Project management & coordination",
      ],
      featured: false,
    },
    {
      id: "flooring",
      title: "Flooring Installation",
      description: "Expert flooring solutions including tile, vinyl, and various specialty finishes for every room in your home.",
      image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/knsp7zct_Flooring%203.jpg",
      features: [
        "Floor tile installation",
        "Luxury vinyl plank (LVP)",
        "Wall tile installation",
        "Subfloor preparation",
        "Transition & trim work",
        "Waterproof flooring solutions",
      ],
      featured: false,
    },
    {
      id: "painting",
      title: "Drywall & Finishing",
      description: "Expert drywall installation and specialty finishes to complete your renovation with a polished look.",
      image: "https://customer-assets.emergentagent.com/job_build-remodel-4/artifacts/36wq056h_D%26F%201.jpg",
      features: [
        "Drywall installation & repair",
        "Backsplash installation",
        "Wall tile finishing",
        "Trim & molding work",
        "Surface preparation",
        "Touch-up & detail work",
      ],
      featured: false,
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              Our Services
            </p>
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-white uppercase tracking-tight mb-6">
              Comprehensive Renovation Services
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl">
              From kitchens to complete home makeovers, we offer a full range of renovation services tailored to your needs and budget.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div 
                key={service.id}
                id={service.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-section-${service.id}`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative overflow-hidden img-zoom">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    {service.featured && (
                      <div className="absolute top-4 left-4 bg-[#d97706] text-white px-4 py-2 font-['Barlow_Condensed'] font-bold uppercase text-sm tracking-wider">
                        Most Popular
                      </div>
                    )}
                  </div>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#0f172a] uppercase tracking-tight mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-8">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#d97706] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  {service.guide && (
                    <Link 
                      to={service.guide}
                      className="inline-flex items-center gap-2 text-[#d97706] hover:text-[#b45309] font-semibold mb-6 transition-colors"
                      data-testid={`service-guide-${service.id}`}
                    >
                      <span>📘 Read: Bathroom Renovation Cost Guide</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                  <div className="flex flex-wrap gap-4">
                    <Link to="/contact">
                      <Button 
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-12 px-8"
                        data-testid={`service-cta-${service.id}`}
                      >
                        Get a Quote
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              How We Work
            </p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-[#0f172a] uppercase tracking-tight">
              Our Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Consultation",
                description: "We meet to discuss your vision, needs, and budget to understand your project fully.",
              },
              {
                step: "02",
                title: "Design & Quote",
                description: "Our team creates a detailed design plan and provides a transparent, itemized quote.",
              },
              {
                step: "03",
                title: "Construction",
                description: "Our skilled craftsmen execute the plan with precision, keeping you informed throughout.",
              },
              {
                step: "04",
                title: "Final Walkthrough",
                description: "We review every detail together to ensure your complete satisfaction.",
              },
            ].map((item, index) => (
              <div 
                key={index}
                className="relative p-8 bg-[#f8fafc] border border-slate-100"
                data-testid={`process-step-${index}`}
              >
                <span className="font-['Barlow_Condensed'] text-6xl font-bold text-[#d97706]/20 absolute top-4 right-4">
                  {item.step}
                </span>
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#0f172a] uppercase mb-3 relative z-10">
                  {item.title}
                </h3>
                <p className="text-slate-600 leading-relaxed relative z-10">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Contact us today for a free consultation and estimate. Let's bring your renovation dreams to life.
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 px-10 btn-glow"
              data-testid="services-cta-btn"
            >
              Schedule Free Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
